import { z } from 'zod';

/**
 * Permission catalog (docs/04-rechtekonzept.md, section 2; PERM-01, PERM-04). Roles are free
 * combinations of these permissions. `externalAllowed: false` means the permission is always
 * removed for external users, whatever their role contains.
 */
type CatalogEntry = { externalAllowed: boolean; group: PermissionGroup; label: string };

export const permissionGroups = {
  admin: 'Administration',
  projects: 'Projekte',
  resources: 'Ressourcen',
  project: 'Projekt',
  tasks: 'Tasks',
  comments: 'Kommentare',
  attachments: 'Anhänge',
  gantt: 'Gantt',
  assistant: 'KI-Assistent',
} as const;
export type PermissionGroup = keyof typeof permissionGroups;

export const globalPermissionCatalog = {
  'admin.users': { externalAllowed: false, group: 'admin', label: 'Benutzer verwalten' },
  'admin.organizations': {
    externalAllowed: false,
    group: 'admin',
    label: 'Kundenorganisationen verwalten',
  },
  'admin.roles': {
    externalAllowed: false,
    group: 'admin',
    label: 'Rollen und Berechtigungen verwalten',
  },
  'admin.integrations': { externalAllowed: false, group: 'admin', label: 'API-Tokens verwalten' },
  'admin.audit': { externalAllowed: false, group: 'admin', label: 'Audit-Log einsehen' },
  'admin.system': { externalAllowed: false, group: 'admin', label: 'Systemübersicht einsehen' },
  'project.create': { externalAllowed: false, group: 'projects', label: 'Projekte anlegen' },
  'project.view_all': {
    externalAllowed: false,
    group: 'projects',
    label: 'Alle Projekte sehen, ohne Mitglied zu sein',
  },
  'project.manage_all_members': {
    externalAllowed: false,
    group: 'projects',
    label: 'Mitglieder in allen Projekten verwalten',
  },
  'resource.view_all': {
    externalAllowed: false,
    group: 'resources',
    label: 'Auslastung aller Personen sehen',
  },
  'resource.edit_all': {
    externalAllowed: false,
    group: 'resources',
    label: 'Buchungen in allen Projekten bearbeiten',
  },
  'job_roles.manage': {
    externalAllowed: false,
    group: 'resources',
    label: 'Fachliche Rollen, Arbeitszeitprofile, Feiertagsregionen und Abwesenheiten pflegen',
  },
  'assistant.use': { externalAllowed: false, group: 'assistant', label: 'KI-Assistent nutzen' },
} as const satisfies Record<string, CatalogEntry>;

export const projectPermissionCatalog = {
  'project.view': { externalAllowed: true, group: 'project', label: 'Projekt sehen' },
  'project.edit': {
    externalAllowed: false,
    group: 'project',
    label: 'Projektdaten, Workflow, Labels bearbeiten',
  },
  'project.manage_members': {
    externalAllowed: false,
    group: 'project',
    label: 'Mitglieder hinzufügen/entfernen, Rollen vergeben',
  },
  'project.invite_external': {
    externalAllowed: false,
    group: 'project',
    label: 'Kunden der Projektorganisation einladen',
  },
  'project.archive': { externalAllowed: false, group: 'project', label: 'Archivieren / löschen' },
  'task.view': { externalAllowed: true, group: 'tasks', label: 'Tasks sehen' },
  'task.view_internal': { externalAllowed: false, group: 'tasks', label: 'Interne Tasks sehen' },
  'task.create': { externalAllowed: true, group: 'tasks', label: 'Tasks anlegen' },
  'task.edit': { externalAllowed: true, group: 'tasks', label: 'Tasks bearbeiten (alle Felder)' },
  'task.edit_status': { externalAllowed: true, group: 'tasks', label: 'Nur Status ändern' },
  'task.assign': { externalAllowed: false, group: 'tasks', label: 'Zuständige ändern' },
  'task.delete': { externalAllowed: false, group: 'tasks', label: 'Tasks löschen' },
  'task.set_visibility': {
    externalAllowed: false,
    group: 'tasks',
    label: 'Sichtbarkeit intern/extern ändern',
  },
  'comment.create': {
    externalAllowed: true,
    group: 'comments',
    label: 'Kommentieren (extern sichtbar)',
  },
  'comment.internal': {
    externalAllowed: false,
    group: 'comments',
    label: 'Interne Kommentare sehen und schreiben',
  },
  'attachment.upload': { externalAllowed: true, group: 'attachments', label: 'Anhänge hochladen' },
  'attachment.internal': {
    externalAllowed: false,
    group: 'attachments',
    label: 'Interne Anhänge sehen und hochladen',
  },
  'gantt.view': { externalAllowed: true, group: 'gantt', label: 'Gantt sehen' },
  'gantt.edit': {
    externalAllowed: false,
    group: 'gantt',
    label: 'Termine und Abhängigkeiten im Gantt ändern',
  },
  'resource.view': {
    externalAllowed: false,
    group: 'resources',
    label: 'Ressourcenbuchungen des Projekts sehen',
  },
  'resource.edit': {
    externalAllowed: false,
    group: 'resources',
    label: 'Ressourcenbuchungen des Projekts bearbeiten',
  },
} as const satisfies Record<string, CatalogEntry>;

export type GlobalPermission = keyof typeof globalPermissionCatalog;
export type ProjectPermission = keyof typeof projectPermissionCatalog;
export type Permission = GlobalPermission | ProjectPermission;

export const globalPermissions = Object.keys(globalPermissionCatalog) as GlobalPermission[]; // keys of a const object
export const projectPermissions = Object.keys(projectPermissionCatalog) as ProjectPermission[]; // keys of a const object

export const roleScopeSchema = z.enum(['global', 'project']);
export type RoleScope = z.infer<typeof roleScopeSchema>;

export const globalPermissionSchema = z.enum(globalPermissions);
export const projectPermissionSchema = z.enum(projectPermissions);
export const permissionSchema = z.enum([...globalPermissions, ...projectPermissions]);

export function isGlobalPermission(value: string): value is GlobalPermission {
  return Object.hasOwn(globalPermissionCatalog, value);
}

export function isProjectPermission(value: string): value is ProjectPermission {
  return Object.hasOwn(projectPermissionCatalog, value);
}

/** Permissions of a scope; used to validate role definitions. */
export function permissionsForScope(scope: RoleScope): readonly Permission[] {
  return scope === 'global' ? globalPermissions : projectPermissions;
}

export function catalogEntry(permission: Permission): CatalogEntry {
  return isGlobalPermission(permission)
    ? globalPermissionCatalog[permission]
    : projectPermissionCatalog[permission];
}

/** PERM-04: false for every permission external users can never hold. */
export function isExternalAllowed(permission: Permission): boolean {
  return catalogEntry(permission).externalAllowed;
}

/** Removes everything external users can never hold (docs/04, section 4, step 3). */
export function stripExternalForbidden<P extends Permission>(permissions: Iterable<P>): Set<P> {
  return new Set([...permissions].filter(isExternalAllowed));
}

/** Permissions that block `externally_assignable` for a role; empty if the role may be given to externals. */
export function externallyForbiddenPermissions(permissions: Iterable<Permission>): Permission[] {
  return [...permissions].filter((p) => !isExternalAllowed(p));
}

/**
 * Permissions that remain in archived projects (docs/04, section 4, step 4). `comment.internal` and
 * `attachment.internal` also grant *reading* internal content, so they stay; services that write
 * comments or attachments must reject archived projects themselves.
 */
export const readOnlyProjectPermissions: ReadonlySet<ProjectPermission> = new Set([
  'project.view',
  'task.view',
  'task.view_internal',
  'comment.internal',
  'attachment.internal',
  'gantt.view',
  'resource.view',
]);

export type DefaultRole = {
  name: string;
  description: string;
  scope: RoleScope;
  externallyAssignable: boolean;
  permissions: readonly Permission[];
};

const observerRole = {
  name: 'Beobachter (intern)',
  description: 'Interne Leserechte inklusive interner Tasks und Kommentare.',
  scope: 'project',
  externallyAssignable: false,
  permissions: [
    'project.view',
    'task.view',
    'task.view_internal',
    'comment.internal',
    'gantt.view',
  ],
} as const satisfies DefaultRole;

/**
 * Internal users with `project.view_all` get these rights in projects they are not a member of
 * (docs/04, section 3).
 */
export const observerPermissions: ReadonlySet<ProjectPermission> = new Set(
  observerRole.permissions,
);

/**
 * Default roles (docs/04, section 3). Created once by a database migration with `is_system = true`
 * and adjustable afterwards; a test checks that the migration matches this list.
 */
export const defaultRoles: readonly DefaultRole[] = [
  {
    name: 'Administration',
    description: 'Alle globalen Berechtigungen.',
    scope: 'global',
    externallyAssignable: false,
    // admin.system is assigned deliberately (ADM-06); break-glass accounts hold it anyway.
    permissions: globalPermissions.filter((p) => p !== 'admin.system'),
  },
  {
    name: 'Ressourcenplanung',
    description: 'Auslastung und Buchungen aller Projekte, fachliche Rollen.',
    scope: 'global',
    externallyAssignable: false,
    permissions: ['resource.view_all', 'resource.edit_all', 'job_roles.manage', 'project.view_all'],
  },
  {
    name: 'Projektleitung (global)',
    description: 'Darf Projekte anlegen.',
    scope: 'global',
    externallyAssignable: false,
    permissions: ['project.create'],
  },
  {
    name: 'KI-Assistent',
    description: 'Darf den KI-Assistenten nutzen (E-24).',
    scope: 'global',
    externallyAssignable: false,
    permissions: ['assistant.use'],
  },
  {
    name: 'Projektleitung',
    description: 'Alle Projekt-Berechtigungen.',
    scope: 'project',
    externallyAssignable: false,
    permissions: projectPermissions,
  },
  {
    name: 'Teammitglied',
    description: 'Mitarbeit im Projekt inklusive interner Inhalte.',
    scope: 'project',
    externallyAssignable: false,
    permissions: [
      'project.view',
      'task.view',
      'task.view_internal',
      'task.create',
      'task.edit',
      'task.edit_status',
      'task.assign',
      'comment.create',
      'comment.internal',
      'attachment.upload',
      'attachment.internal',
      'gantt.view',
      'resource.view',
    ],
  },
  observerRole,
  {
    name: 'Kunde – Mitarbeit',
    description: 'Kunden dürfen Tasks anlegen, freigeben und kommentieren.',
    scope: 'project',
    externallyAssignable: true,
    permissions: [
      'project.view',
      'task.view',
      'task.create',
      'task.edit_status',
      'comment.create',
      'attachment.upload',
      'gantt.view',
    ],
  },
  {
    name: 'Kunde – Lesen',
    description: 'Kunden dürfen extern sichtbare Inhalte lesen.',
    scope: 'project',
    externallyAssignable: true,
    permissions: ['project.view', 'task.view', 'gantt.view'],
  },
];

/** Content visibility (tasks, comments, attachments, activity). */
export const visibilitySchema = z.enum(['internal', 'external']);
export type Visibility = z.infer<typeof visibilitySchema>;

/** GET /api/v1/projects/:projectId/permissions – effective rights, for UI hints only. */
export const projectPermissionsResponseSchema = z.object({
  permissions: z.array(projectPermissionSchema),
  visibilities: z.array(visibilitySchema),
});
export type ProjectPermissionsResponse = z.infer<typeof projectPermissionsResponseSchema>;
